import { FC, useState, useMemo } from 'react';
import { useStore } from 'hooks/useRootStore';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useQuery } from 'react-query';
import { Grid, Paper } from '@material-ui/core';
import { GridColumns, GridCellParams, DataGrid } from '@material-ui/data-grid';
import { useStyles } from './styles';
import { observer } from 'mobx-react-lite';
import EmailIcon from '../../../assets/icons/EndUser/Email.svg';
import { getRowsPerPageOptions } from 'utils/pagination';
import { Search } from 'components/Search';
import IconButton from '@material-ui/core/IconButton';
import { Button } from '@material-ui/core';
import HomeWorkIcon from '@material-ui/icons/HomeWork';
import { useMutation } from 'react-query';
import { UserDepartmentData } from './types';
import { handleCompanyUrl } from 'utils/organizationUtils';
import { useHistory } from 'react-router';
import { useForm } from 'react-hook-form';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';

export const ManagerDepartmentTable: FC<any> = observer(() => {
  const {
    departmentTableStore: { getManagerDepartment },
    ldapStore: { updateManager, usersView, getUsers, setUsersKeywordFilter },
    adminsTableStore: { getAdmins, adminsView },
    notificationStore: { openNotification },
  } = useStore();
  const history = useHistory();
  const classes = useStyles();
  const [currentDepartment] = useState<any>(JSON.parse(localStorage.getItem('CurrentDepartment') as string));
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const { handleSubmit } = useForm<UserDepartmentData>();

  const getUsersReq = useQuery('getUsersReq', () => getUsers(), {
    onError: () => openNotification('error', `Can't get users`),
  });
  const adminsReq = useQuery('adminsReq', () => getAdmins(), {
    onError: (error: any) => openNotification('error', error?.message || 'Failed to fetch admins'),
  });
  const getManagerDepartmentReq = useQuery('getManagerDepartment', () => getManagerDepartment(currentDepartment?.dn), {
    onError: (error: Error) => openNotification('error', error.message),
  });
  const entityId = currentDepartment?.department_id || currentDepartment?.id;
  const backRoute = '/department/users?value=two';
  const entityName = currentDepartment?.ou;
  const updateManagerReq = useMutation((managerIds: number[]) => updateManager('department', entityId, managerIds), {
    mutationKey: 'updateManagerReq',
    onSuccess: async () => {
      openNotification('success', 'Saved!!');
      localStorage.setItem('activeTab', 'two');
      history.push(handleCompanyUrl(backRoute));
    },
    onError: (error: any) => openNotification('error', error?.message || `Can't update managers`),
  });
  // const managerIdsFromAPI = getManagerDepartmentReq?.data ?? [];
  // const managerSet = useMemo(() => {
  //     return new Set((managerIdsFromAPI ?? []).map((m: any) => m.user_id));
  // }, [managerIdsFromAPI]);
  const managerSet = useMemo(() => {
    const managerIdsFromAPI = getManagerDepartmentReq?.data ?? [];
    return new Set(managerIdsFromAPI.map((m: any) => m.user_id));
  }, [getManagerDepartmentReq?.data]);

  const row = useMemo(() => {
    if (getUsersReq.isFetching || adminsReq.isFetching) return [];
    const allUsers = [...usersView, ...adminsView];
    return allUsers
      .filter((u) => !managerSet.has(u.user_id))
      .map((u) => ({
        ...u,
        id: u.user_id,
      }));
  }, [getUsersReq.isFetching, adminsReq.isFetching, usersView, adminsView, managerSet]);
  const handleSelectionModelChange = (selection: any) => {
    setSelectedRows(selection.selectionModel as string[]);
  };
  const onSubmit = () => {
    const managerIds = selectedRows
      .map((rowId) => {
        const item = row.find((r) => r.id === Number(rowId));
        return item?.user_id;
      })
      .filter((id): id is number => id !== undefined);
    updateManagerReq.mutate(managerIds);
  };
  const [pageSize, setPageSize] = useState(10);
  const rowsPerPageOptions = useMemo(() => getRowsPerPageOptions(row), [row]);
  const columns: GridColumns = [
    { field: 'cn', headerName: 'Full Name', flex: 0.5 },
    {
      field: 'mail',
      headerName: 'EMAIL',
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const value = String(params.value ?? '');
        return (
          <div style={{ display: 'flex', gap: '10px' }}>
            <img src={EmailIcon} alt='' />
            {value}
          </div>
        );
      },
    },
  ];
  useAppPreloader(
    getUsersReq.isFetching || updateManagerReq.isLoading || adminsReq.isFetching || getManagerDepartmentReq.isFetching
  );
  return (
    <>
      <Grid container alignItems='center' className={classes.box}>
        <IconButton
          onClick={() => {
            history.push(handleCompanyUrl('/department/users'));
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <h1> Please select one or more User Accounts below to add to the Department ({entityName}) selected.</h1>
      </Grid>
      <Grid container justify='space-between' alignItems='center' className={classes.box}>
        <IconButton aria-label='home' size='small'>
          <HomeWorkIcon />
        </IconButton>
        <Search handelSearch={setUsersKeywordFilter} />
        {!!selectedRows.length && (
          <Button variant='contained' color='primary' style={{ margin: '0 10px' }} onClick={handleSubmit(onSubmit)}>
            Assign as Manager
          </Button>
        )}
      </Grid>
      {getUsersReq.isFetching === false && (
        <>
          {row.length === 0 ? (
            <h1 className={classes.link}> All the users are already linked with this department. </h1>
          ) : (
            <Paper className={classes.dataGrid} elevation={0}>
              <DataGrid
                checkboxSelection
                className={classes.noBorder}
                autoHeight
                rows={row}
                columns={columns}
                pageSize={pageSize}
                rowsPerPageOptions={rowsPerPageOptions}
                pagination
                onPageChange={(params) => setPageSize(params.pageSize)}
                selectionModel={selectedRows}
                onSelectionModelChange={handleSelectionModelChange}
              />
            </Paper>
          )}
        </>
      )}
    </>
  );
});
